import Link from "next/link";
import { useRouter } from "next/router";
import styles from "./header.module.scss";
import { headerItems } from "@/lib/header";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import MegaMenu from "../megaMenu/megaMenu";
import RoundedButton from "../roundedButton/roundedButton";
import LanguageSwitcher from "../languageSwitcher/languageSwitcher";
import Image from "next/image";
import { useWordPressApi, useWordPressData } from "@/hooks/useWordPressApi";

// TypeScript interfaces
interface WordPressPost {
  id: number;
  slug: string;
  acf: {
    hero_title: string;
    menu_desc: string;
    isdynamic: boolean;
  };
}

interface ImagesData {
  [key: string]: string;
}

const Header: React.FC = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number>(0);
  const [allImages, setAllImages] = useState<ImagesData | null>(null);
  const previousOverflowRef = useRef<string>("");
  const mobileNavRef = useRef<HTMLElement | null>(null);

  // Use WordPress API hook
  const { getSolutions, getProducts, currentLanguage } = useWordPressApi();

  // Fetch solutions data with proper error handling
  const {
    data: solutionsData,
    loading: solutionsLoading,
    error: solutionsError,
  } = useWordPressData<WordPressPost[]>(
    () => getSolutions() as Promise<WordPressPost[]>,
    [currentLanguage],
  );

  // Fetch products data with proper error handling
  const {
    data: productsData,
    loading: productsLoading,
    error: productsError,
  } = useWordPressData<WordPressPost[]>(
    () => getProducts() as Promise<WordPressPost[]>,
    [currentLanguage],
  );

  const isMenoPage = router.pathname.endsWith("/meno-digital-menu");

  // Load images data once
  useEffect(() => {
    const loadImages = async () => {
      try {
        const svgData = await fetch("/images.json").then((res) => res.json());
        setAllImages(svgData);
      } catch (error) {
        console.error("Error loading images:", error);
      }
    };

    loadImages();
  }, []);

  const handleChange =
    (panel: number) => (event: React.SyntheticEvent, expanded: boolean) => {
      setExpanded(expanded);
      setSelectedId(panel);
    };

  const handleMenuOpen = (): void => {
    setMenuOpen(!menuOpen);
  };

  const handleDataFromChild = (data: boolean): void => {
    setIsOpen(data);
  };

  // Prevent body scroll when the mobile menu is open
  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const { body } = document;

    if (menuOpen) {
      previousOverflowRef.current = body.style.overflow;
      body.style.overflow = "hidden";
    } else {
      body.style.overflow = previousOverflowRef.current || "";
    }

    return () => {
      body.style.overflow = previousOverflowRef.current || "";
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    const prefersReducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;
    if (prefersReducedMotion) {
      return;
    }

    let revert: (() => void) | undefined;
    (async () => {
      const nav = mobileNavRef.current;
      if (!nav) {
        return;
      }

      const links = nav.querySelectorAll("a");
      if (!links.length) {
        return;
      }

      const { gsap } = await import("gsap");
      const ctx = gsap.context(() => {
        gsap.set(links, { opacity: 0, y: 12 });
        gsap.to(links, {
          opacity: 1,
          y: 0,
          duration: 0.45,
          ease: "power2.out",
          stagger: 0.07,
          delay: 0.05,
        });
      }, nav);

      revert = () => ctx.revert();
    })();

    return () => {
      revert?.();
    };
  }, [menuOpen]);

  // Show error state if there are API errors
  if (solutionsError || productsError) {
    console.error("Header API Error:", solutionsError || productsError);
  }

  return (
    <>
      <div
        className={`${styles["overlay"]} ${isOpen ? styles["show"] : ""}`}
      ></div>
      <header className={`${styles["header"]} `} style={{ direction: "ltr" }}>
        <div className={`custom-container`}>
          <div className="inner">
            {/* Desktop Version */}
            <div className={`${styles["headerContainer"]} d-flex-lg d-none-xs`}>
              <div className={`${styles["left"]}`}>
                <Link href="/en/">
                  <Image
                    src="/images/qrhub-dark-logo.svg"
                    alt="QRHUB - Best Static and Dynamic QR Code Solutions"
                    width={120}
                    height={30}
                    priority
                  />
                </Link>
              </div>

              <div
                className={`${styles["center"]}`}
                onMouseLeave={() => {
                  setIsOpen(false);
                }}
              >
                <nav>
                  {headerItems.mainMenu.items &&
                    headerItems.mainMenu.items.map((itemLink, i) => (
                      <div
                        key={`menu-item-${i}`}
                        className={`${styles["mainMenuItem"]}`}
                      >
                        {headerItems.mainMenu.items[i].subItems ? (
                          <Link
                            className={`${styles["mainMenuItemLink"]}  ${
                              isOpen ? styles["open"] : ""
                            }`}
                            key={`link-${t(itemLink.title)}`}
                            href={t(itemLink.url)}
                            onMouseEnter={() => {
                              setIsOpen(true);
                            }}
                            onClick={() => {
                              setIsOpen(false);
                            }}
                          >
                            {t(itemLink.title)}
                          </Link>
                        ) : (
                          <Link
                            className={`${styles["mainMenuItemLink"]}`}
                            key={`link-${t(itemLink.title)}`}
                            href={t(itemLink.url)}
                            onMouseEnter={() => {
                              setIsOpen(false);
                            }}
                          >
                            {t(itemLink.title)}
                          </Link>
                        )}

                        {headerItems.mainMenu.items[i].subItems ? (
                          <>
                            <div
                              className={`${styles["dropdownList"]} ${
                                isOpen ? styles["open"] : ""
                              }`}
                            >
                              {t(itemLink.title) === "Products" ? (
                                <>
                                  {productsData && (
                                    <MegaMenu
                                      menuItems={productsData}
                                      isSolutions={false}
                                      sendDataToParent1={handleDataFromChild}
                                    />
                                  )}
                                </>
                              ) : t(itemLink.title) === "Solutions" ? (
                                <>
                                  {solutionsData && (
                                    <MegaMenu
                                      menuItems={solutionsData}
                                      isSolutions={true}
                                      sendDataToParent1={handleDataFromChild}
                                    />
                                  )}
                                </>
                              ) : (
                                headerItems.mainMenu.items[i].subItems?.map(
                                  (subItemLink, subIndex) => (
                                    <Link
                                      key={`sublink-${subIndex}-${t(
                                        subItemLink.title,
                                      )}`}
                                      href={t(subItemLink.url)}
                                    >
                                      {t(subItemLink.title)}
                                    </Link>
                                  ),
                                )
                              )}
                            </div>
                          </>
                        ) : (
                          ""
                        )}
                      </div>
                    ))}
                </nav>
                {/* <LanguageSwitcher /> */}
                <Link
                  href="/en/meno-digital-menu/"
                  className="inline-block bg-[#FF00A4]/10 text-[#FF00A4] text-sm font-semibold px-4 py-1.5 rounded-full mt-3 ml-5"
                >
                  meno
                </Link>
              </div>

              <div className={`${styles["desktopRight"]}`}>
                <RoundedButton
                  title="Login"
                  url={
                    isMenoPage
                      ? "https://dashboard.qrhub.io/account/login?meno=true"
                      : `${process.env.NEXT_PUBLIC_DASHBOARD_URL}/account/login`
                  }
                  light={false}
                  fill={false}
                  withIcon={false}
                  target="_blank"
                />
                <RoundedButton
                  title="Start for Free"
                  url={
                    isMenoPage
                      ? "https://dashboard.qrhub.io/account/create?meno=true"
                      : `${process.env.NEXT_PUBLIC_DASHBOARD_URL}/account/create`
                  }
                  light={false}
                  fill={true}
                  target="_blank"
                  menoHover={isMenoPage}
                />
              </div>
            </div>
            {/* Mobile Version */}
            <div className={`${styles["headerContainer"]} d-none-lg d-flex-xs`}>
              <div className={`${styles["left"]}`}>
                <Link href="/en/">
                  <Image
                    src="/images/qrhub-dark-logo.svg"
                    alt="QRHUB - Best Static and Dynamic QR Code Solutions"
                    width={120}
                    height={30}
                    priority
                  />
                </Link>
              </div>

              <div className={`${styles["mobileRight"]}`}>
                <RoundedButton
                  title="Login"
                  url={
                    isMenoPage
                      ? "https://dashboard.qrhub.io/account/login?meno=true"
                      : `${process.env.NEXT_PUBLIC_DASHBOARD_URL}/account/login`
                  }
                  light={false}
                  fill={false}
                  withIcon={false}
                  target="_blank"
                  menoHover={isMenoPage}
                />
                <button
                  className={`${styles["hamburger"]} ${
                    menuOpen ? styles["active"] : ""
                  }`}
                  onClick={handleMenuOpen}
                  aria-label="Toggle menu"
                >
                  <span></span>
                  <span></span>
                  <span></span>
                </button>
              </div>
            </div>

            {/* Mobile Menu Overlay */}
            {menuOpen && (
              <div
                className={`${styles["mobileMenuOverlay"]}`}
                onClick={handleMenuOpen}
              >
                <div
                  className={`${styles["mobileMenu"]}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <nav className={`${styles["mobileNav"]}`} ref={mobileNavRef}>
                    {headerItems.mainMenu.items &&
                      headerItems.mainMenu.items.map((itemLink, i) => (
                        <Link
                          key={`mobile-link-${i}`}
                          className={`${styles["mobileMenuItem"]}`}
                          href={t(itemLink.url)}
                          onClick={handleMenuOpen}
                        >
                          {t(itemLink.title)}
                        </Link>
                      ))}
                    <Link
                      href="/en/meno-digital-menu/"
                      className={`${styles["mobileMenoBanner"]}`}
                      onClick={handleMenuOpen}
                    >
                      <span className={`${styles["mobileMenoText"]}`}>
                        Discover
                      </span>
                      <span className={`${styles["mobileMenoBrand"]}`}>
                        <Image
                          src="/images/meno/meno-logo.svg"
                          alt="Meno"
                          width={70}
                          height={26}
                        />
                      </span>
                    </Link>
                  </nav>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
